import { z } from 'zod';
import { AdminCategory, AdminEntity, AdminPayMethod, CashDirection, CashEntryStatus, IgnoreReason, PayableKind, PayableStatus, RecurringCycle, TaxKind, TaxSource, TaxStatus, TxMatchKind, TxSide, UsciteSource, } from '../enums.js';
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data attesa nel formato YYYY-MM-DD');
const trim = z.string().trim();
const money = z.coerce.number().min(0).max(99_999_999);
const vatRate = z.coerce.number().min(0).max(100).default(22);
const boolFromQuery = z.preprocess((v) => (typeof v === 'string' ? v === 'true' || v === '1' : v), z.boolean());
/**
 * `includeHistory=true` disattiva la data di taglio (OrgSettings.adminCutoffDate)
 * e rimette in lista anche i documenti degli anni passati. E' l'interruttore
 * "mostra lo storico" della UI: niente viene cancellato, solo nascosto.
 */
export const includeHistory = boolFromQuery.optional();
export const historyQuery = z.object({ includeHistory });
// ─── Integrazioni ───────────────────────────────────────────────────
export const qontoConnectInput = z.object({
    login: trim.min(1).max(200),
    secret: trim.min(1).max(500),
    baseUrl: z.string().url().optional().nullable(),
});
// ─── Cestino (documenti tolti dal gestionale) ───────────────────────
/**
 * Cestinare = togliere un documento dal gestionale, non annullarlo. Serve per le
 * fatture che nel gestionale non devono starci: quelle scartate dallo SdI (per il
 * fisco non sono mai esistite), i doppioni, gli errori. Su FattureInCloud non si
 * scrive niente e niente viene cancellato dal database: il documento va nel
 * cestino (`deletedAt` + motivo) e da li' si puo' ripristinare.
 *
 * Il motivo e' obbligatorio: senza, tra sei mesi nessuno ricorda perche' una
 * fattura e' sparita. La nota e' il posto dove mettere la fonte (es. la mail del
 * commercialista).
 */
export const ignoreDocInput = z.object({
    reason: z.nativeEnum(IgnoreReason),
    note: trim.max(500).optional().nullable(),
});
/** Cestina piu' documenti in un colpo (la selezione multipla degli Incassi). */
export const ignoreDocsInput = ignoreDocInput.extend({
    ids: z.array(z.string().min(1)).min(1).max(200),
});
/** Ripristina piu' documenti (l'Annulla del toast dopo una cestinata multipla). */
export const restoreDocsInput = z.object({
    ids: z.array(z.string().min(1)).min(1).max(200),
});
// ─── Incassi (rate fatture emesse) ──────────────────────────────────
// `cestinate` e' la vista cestino: elenca le FATTURE cestinate (non le rate) con
// motivo e data, per poterle ripristinare.
export const IncassiStatusFilter = { APERTE: 'aperte', INCASSATE: 'incassate', TUTTE: 'tutte', CESTINATE: 'cestinate' };
export const incassiQuery = z.object({
    status: z.nativeEnum(IncassiStatusFilter).default(IncassiStatusFilter.APERTE),
    from: isoDate.optional(),
    to: isoDate.optional(),
    clientId: z.string().optional(),
    search: trim.optional(),
    includeHistory,
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(500).default(50),
});
// syncToFic: se la fattura vive su FattureInCloud aggiorna anche la sua payments_list.
//
// Decisione di Lorenzo (19/09/2026): un pagamento si chiude SOLO dentro Masamune
// OS, su FattureInCloud non si scrive. Il default e' quindi false ovunque e la
// UI non manda piu' questo campo: resta solo come scappatoia esplicita via API,
// perche' nessuno possa scrivere su FiC per sbaglio. La chiusura sopravvive al
// sync grazie a InvoicePayment.localPaidAt (vedi schema.prisma).
export const markPaidInput = z.object({
    paidDate: isoDate,
    method: z.nativeEnum(AdminPayMethod).default(AdminPayMethod.BONIFICO),
    accountId: z.string().optional().nullable(),
    bankTransactionId: z.string().optional().nullable(),
    // Perche' la rata e' stata chiusa qui: finisce in InvoicePayment.localPaidNote
    // ed e' quello che si legge nella vista "Chiuse qui".
    localPaidNote: trim.max(500).optional().nullable(),
    syncToFic: z.boolean().default(false),
});
export const unmarkPaidInput = z.object({
    syncToFic: z.boolean().default(false),
});
// ─── Chiusure locali (rate chiuse qui, su FiC ancora aperte) ────────
export const chiusureLocaliQuery = z.object({
    search: trim.optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(500).default(50),
});
// ─── Uscite unificate ───────────────────────────────────────────────
export const UsciteStatusFilter = { APERTE: 'aperte', PAGATE: 'pagate', TUTTE: 'tutte' };
export const usciteQuery = z.object({
    status: z.nativeEnum(UsciteStatusFilter).default(UsciteStatusFilter.APERTE),
    from: isoDate.optional(),
    to: isoDate.optional(),
    source: z.nativeEnum(UsciteSource).optional(),
    search: trim.optional(),
    entity: z.nativeEnum(AdminEntity).optional(),
    includeHistory,
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(500).default(50),
});
// RECURRING: `id` e' la RecurringExpense e `dueDate` l'occorrenza che si sta pagando
// (default: la nextDueDate). TAX: `id` e' la TaxInstallment. CASH: la CashEntry OUT.
export const uscitePayInput = z.object({
    source: z.nativeEnum(UsciteSource),
    id: z.string().min(1),
    dueDate: isoDate.optional(),
    paidDate: isoDate,
    method: z.nativeEnum(AdminPayMethod).optional(),
    amount: money.optional(),
    accountId: z.string().optional().nullable(),
    bankTransactionId: z.string().optional().nullable(),
    // Solo per source PAYABLE: motivo della chiusura locale (le altre sorgenti
    // — tasse, ricorrenti, contanti — non stanno su FiC e non rischiano nulla).
    localPaidNote: trim.max(500).optional().nullable(),
    syncToFic: z.boolean().default(false),
});
// ─── Fatture ricevute (Payable) ─────────────────────────────────────
export const payablePaymentInput = z.object({
    dueDate: isoDate,
    amount: money,
    paidDate: isoDate.optional().nullable(),
    method: z.nativeEnum(AdminPayMethod).optional().nullable(),
    accountId: z.string().optional().nullable(),
    note: trim.max(500).optional().nullable(),
});
export const createPayableInput = z.object({
    vendorName: trim.min(1).max(200),
    supplierId: z.string().optional().nullable(),
    number: trim.max(60).optional().nullable(),
    kind: z.nativeEnum(PayableKind).default(PayableKind.EXPENSE),
    issueDate: isoDate,
    dueDate: isoDate.optional().nullable(),
    net: money,
    vat: money.default(0),
    total: money.optional(),
    category: trim.max(80).optional().nullable(),
    description: trim.max(500).optional().nullable(),
    entity: z.nativeEnum(AdminEntity).default(AdminEntity.MASAMUNE),
    note: trim.max(2000).optional().nullable(),
    // Se manca, viene creata una rata unica con scadenza dueDate (o issueDate).
    payments: z.array(payablePaymentInput).optional(),
});
// `status` si passa a mano solo per marcare/smarcare FINANZIATA (le auto a rate):
// gli altri stati restano derivati dalle rate. `recurringExpenseId` e' la rata
// ricorrente che sta pagando il documento finanziato.
export const updatePayableInput = createPayableInput.omit({ payments: true }).partial().extend({
    status: z.nativeEnum(PayableStatus).optional(),
    recurringExpenseId: z.string().optional().nullable(),
});
// `finanziate` = le fatture gia' pagate dalla finanziaria: stanno in una sezione a
// parte e non subiscono la data di taglio (il finanziamento e' in corso oggi,
// qualunque sia l'anno della fattura).
// `cestinate` = il cestino: i documenti tolti dal gestionale, con motivo e data.
export const payablesQuery = z.object({
    status: z.enum(['aperte', 'pagate', 'finanziate', 'cestinate', 'tutte']).default('aperte'),
    from: isoDate.optional(),
    to: isoDate.optional(),
    search: trim.optional(),
    supplierId: z.string().optional(),
    entity: z.nativeEnum(AdminEntity).optional(),
    includeHistory,
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(500).default(50),
});
// ─── Uscite ricorrenti ──────────────────────────────────────────────
export const createRecurringInput = z.object({
    name: trim.min(1).max(160),
    counterparty: trim.max(160).optional().nullable(),
    amountNet: money,
    vatRate,
    cycle: z.nativeEnum(RecurringCycle).default(RecurringCycle.MONTHLY),
    dayOfMonth: z.coerce.number().int().min(1).max(31).default(1),
    startDate: isoDate,
    endDate: isoDate.optional().nullable(),
    nextDueDate: isoDate.optional(),
    active: z.boolean().default(true),
    category: z.nativeEnum(AdminCategory).default(AdminCategory.ALTRO),
    method: z.nativeEnum(AdminPayMethod).default(AdminPayMethod.BONIFICO),
    matchRule: trim.max(200).optional().nullable(),
    entity: z.nativeEnum(AdminEntity).default(AdminEntity.MASAMUNE),
    note: trim.max(2000).optional().nullable(),
});
export const updateRecurringInput = createRecurringInput.partial();
export const recurringQuery = z.object({
    active: boolFromQuery.optional(),
    category: z.nativeEnum(AdminCategory).optional(),
    entity: z.nativeEnum(AdminEntity).optional(),
    search: trim.optional(),
});
// ─── Ricavi ricorrenti ──────────────────────────────────────────────
// Speculare alle uscite ricorrenti. `clientId` manca sulle voci aggregate
// (GoDeal, Stripe, hosting): in quel caso vale `counterparty`. `endDate` e' la
// fine del contratto: dopo quella data non si prevede piu' nessun incasso.
export const createRecurringRevenueInput = z.object({
    name: trim.min(1).max(160),
    clientId: z.string().optional().nullable(),
    // Il progetto che questo canone fattura, quando il lavoro e' una commessa e
    // non un abbonamento. Resta `null` sulle voci aggregate e sull'affitto AMA,
    // che una commessa non ce l'hanno. Collegarlo NON sposta un euro: il canone
    // continua a generare le occorrenze della previsione di cassa.
    projectId: z.string().optional().nullable(),
    counterparty: trim.max(160).optional().nullable(),
    amountNet: money,
    vatRate,
    cycle: z.nativeEnum(RecurringCycle).default(RecurringCycle.MONTHLY),
    dayOfMonth: z.coerce.number().int().min(1).max(31).default(1),
    startDate: isoDate,
    endDate: isoDate.optional().nullable(),
    nextDueDate: isoDate.optional(),
    active: z.boolean().default(true),
    category: z.nativeEnum(AdminCategory).default(AdminCategory.CLIENTI),
    method: z.nativeEnum(AdminPayMethod).default(AdminPayMethod.BONIFICO),
    matchRule: trim.max(200).optional().nullable(),
    entity: z.nativeEnum(AdminEntity).default(AdminEntity.MASAMUNE),
    note: trim.max(2000).optional().nullable(),
});
export const updateRecurringRevenueInput = createRecurringRevenueInput.partial();
export const recurringRevenueQuery = z.object({
    active: boolFromQuery.optional(),
    category: z.nativeEnum(AdminCategory).optional(),
    clientId: z.string().optional(),
    entity: z.nativeEnum(AdminEntity).optional(),
    // true = solo le voci con un contratto che finisce entro 90 giorni
    endingSoon: boolFromQuery.optional(),
    search: trim.optional(),
});
// Incasso di un'occorrenza: crea una CashEntry IN PAGATO collegata e fa
// avanzare nextDueDate di un ciclo (speculare a /admin/uscite/pay RECURRING).
export const recurringRevenueCollectInput = z.object({
    dueDate: isoDate.optional(),
    paidDate: isoDate,
    method: z.nativeEnum(AdminPayMethod).optional(),
    amount: money.optional(),
    accountId: z.string().optional().nullable(),
    bankTransactionId: z.string().optional().nullable(),
});
// ─── Tasse e F24 ────────────────────────────────────────────────────
export const taxInstallmentInput = z.object({
    dueDate: isoDate,
    amount: money,
    paidAt: isoDate.optional().nullable(),
    method: z.nativeEnum(AdminPayMethod).optional().nullable(),
    accountId: z.string().optional().nullable(),
    note: trim.max(500).optional().nullable(),
});
export const createTaxInput = z.object({
    kind: z.nativeEnum(TaxKind),
    year: z.coerce.number().int().min(2000).max(2100),
    period: trim.max(20).optional().nullable(),
    description: trim.min(1).max(200),
    amountDue: money,
    dueDate: isoDate.optional().nullable(),
    source: z.nativeEnum(TaxSource).default(TaxSource.CONFERMATO),
    entity: z.nativeEnum(AdminEntity).default(AdminEntity.MASAMUNE),
    note: trim.max(2000).optional().nullable(),
    // Se manca, viene creata una rata unica per amountDue con scadenza dueDate.
    installments: z.array(taxInstallmentInput).optional(),
});
export const updateTaxInput = createTaxInput.omit({ installments: true }).partial();
export const taxesQuery = z.object({
    year: z.coerce.number().int().optional(),
    kind: z.nativeEnum(TaxKind).optional(),
    status: z.nativeEnum(TaxStatus).optional(),
    source: z.nativeEnum(TaxSource).optional(),
    entity: z.nativeEnum(AdminEntity).optional(),
});
// ─── Voci manuali: previsioni e contanti ────────────────────────────
export const createCashEntryInput = z.object({
    direction: z.nativeEnum(CashDirection),
    counterparty: trim.min(1).max(160),
    description: trim.max(500).optional().nullable(),
    amountNet: money,
    vatRate: z.coerce.number().min(0).max(100).default(0),
    expectedDate: isoDate,
    status: z.nativeEnum(CashEntryStatus).default(CashEntryStatus.PREVISTO),
    paidAt: isoDate.optional().nullable(),
    method: z.nativeEnum(AdminPayMethod).default(AdminPayMethod.CONTANTI),
    accountId: z.string().optional().nullable(),
    category: z.nativeEnum(AdminCategory).default(AdminCategory.ALTRO),
    entity: z.nativeEnum(AdminEntity).default(AdminEntity.MASAMUNE),
    invoiceId: z.string().optional().nullable(),
    note: trim.max(2000).optional().nullable(),
});
export const updateCashEntryInput = createCashEntryInput.partial();
export const cashEntriesQuery = z.object({
    direction: z.nativeEnum(CashDirection).optional(),
    status: z.nativeEnum(CashEntryStatus).optional(),
    method: z.nativeEnum(AdminPayMethod).optional(),
    accountId: z.string().optional(),
    entity: z.nativeEnum(AdminEntity).optional(),
    from: isoDate.optional(),
    to: isoDate.optional(),
    search: trim.optional(),
    includeHistory,
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(500).default(50),
});
// ─── Conti ──────────────────────────────────────────────────────────
export const updateAccountInput = z.object({
    name: trim.min(1).max(120).optional(),
    openingBalance: z.coerce.number().min(-99_999_999).max(99_999_999).optional(),
});
// ─── Movimenti Qonto ────────────────────────────────────────────────
export const transactionsQuery = z.object({
    accountId: z.string().optional(),
    from: isoDate.optional(),
    to: isoDate.optional(),
    side: z.nativeEnum(TxSide).optional(),
    unmatched: boolFromQuery.optional(),
    operationType: trim.optional(),
    search: trim.optional(),
    includeHistory,
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(500).default(50),
});
export const matchTransactionInput = z.object({
    kind: z.nativeEnum(TxMatchKind),
    id: z.string().min(1),
    // Se la rata/voce e' ancora aperta la segna pagata alla data del movimento.
    markPaid: z.boolean().default(true),
    syncToFic: z.boolean().default(false),
});
export const suggestionsQuery = z.object({
    paymentId: z.string().min(1),
    kind: z.nativeEnum(TxMatchKind).default(TxMatchKind.INVOICE_PAYMENT),
});
// ─── Previsione di cassa ────────────────────────────────────────────
export const cashForecastQuery = z.object({
    from: isoDate.optional(),
    to: isoDate.optional(),
    includeHistory,
});
//# sourceMappingURL=admin.js.map